import { encode } from 'js-base64';

import { createAsyncThunk } from '@reduxjs/toolkit';

import loggerGenerator from '@travel/logger';

import { apiPath } from 'constants/api';
import { postInHouseToken, postLogout, refreshToken } from './api';

import type { RootState } from 'react-redux';
import type { AuthCodeError, AuthCodeItem, ThunkExtra } from './types';
import type { UserProfile } from '../apiTypes';
import type { ApiClient } from '@travel/api-client';
import { noticeError } from '@travel/tracker';
const logger = loggerGenerator();

export const fetchAuthCode = createAsyncThunk<
  AuthCodeItem,
  string,
  { state: RootState; extra: ThunkExtra; rejectValue: AuthCodeError }
>('authCode/fetch', async (fIdSession: string, { extra, rejectWithValue }) => {
  const { apiClient } = extra;

  try {
    const authCode = await postInHouseToken(apiClient, encode(fIdSession));

    return authCode;
  } catch (error) {
    logger.error('Auth Code Fetch Failed', error);
    noticeError('Auth Code Fetch Failed', error);

    return rejectWithValue(error as AuthCodeError);
  }
});

export const refreshAuthCode = createAsyncThunk<
  AuthCodeItem,
  void,
  { state: RootState; extra: ThunkExtra; rejectValue: AuthCodeError }
>('authCode/refresh', async (_arg, { getState, extra, rejectWithValue }) => {
  const { apiClient } = extra;
  const {
    _httpRequest: { normalizedCookies },
    authCode: { item },
  } = getState();

  if (!item?.token) return;

  try {
    const authCode = await refreshToken(apiClient, encode(normalizedCookies.f_id_session.value));

    return authCode;
  } catch (error) {
    logger.error('Refresh Token Failed', error);
    noticeError('Refresh Token Failed', error);

    return rejectWithValue(error as AuthCodeError);
  }
});

export const logout = createAsyncThunk<
  AuthCodeItem,
  void,
  { extra: ThunkExtra; rejectValue: AuthCodeError }
>('authCode/logout', async (_, { rejectWithValue, extra }) => {
  const { apiClient } = extra;

  try {
    await postLogout(apiClient);

    return { token: '' };
  } catch (error) {
    logger.error('Logout Failed', error);
    noticeError('Logout Failed', error);

    return rejectWithValue(error as AuthCodeError);
  }
});

export const fetchLoggedInUser = createAsyncThunk<
  UserProfile,
  void,
  { state: RootState; extra: { apiClient: ApiClient }; rejectValue: AuthCodeError }
>('userProfile/fetch', async (_, { getState, extra: { apiClient }, rejectWithValue }) => {
  const {
    authCode: { item },
  } = getState();

  if (!item?.token) return;

  try {
    const data = await apiClient.get(apiPath.getUserProfile.pathname);

    return data;
  } catch (error) {
    logger.error('Fetch Logged In User Failed', error);
    noticeError('Fetch Logged In User Failed', error);

    return rejectWithValue(error);
  }
});
